/**
 * Datetime conversion helpers for the FormField `datetime` kind
 * (P74.1).
 *
 * Browser <input type="datetime-local"> emits + accepts strings
 * of shape `YYYY-MM-DDTHH:MM` (no seconds, no timezone). The
 * backend's Pydantic schemas expect ISO 8601 with timezone. We
 * append `:00Z` on submit (treating the local value as UTC) and
 * strip the suffix on display.
 *
 * Operators in non-UTC zones land a different absolute time than
 * they may expect; a timezone picker is a future polish phase.
 * Documented in the P74 plan.
 */

/** Strip seconds + timezone for display in datetime-local. */
export function isoToDatetimeLocal(iso: string | null | undefined): string {
  if (!iso) return "";
  // Match YYYY-MM-DDTHH:MM at the start; ignore trailing seconds + zone.
  const m = iso.match(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2})/);
  return m?.[1] ?? "";
}

/** Add `:00Z` to a datetime-local value to produce an ISO 8601 UTC string. */
export function datetimeLocalToIso(value: string | null | undefined): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  // Already has seconds + zone? Pass through.
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(trimmed)) {
    return trimmed.endsWith("Z") ? trimmed : `${trimmed}Z`;
  }
  // Minutes only -> append :00Z.
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(trimmed)) {
    return `${trimmed}:00Z`;
  }
  return trimmed;
}
